import React from 'react';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { PERMISSION_ACTIONS, DATA_SCOPE_KEYS, MODULE_ICONS } from '../../constants';

const PermissionMatrix = ({ catalog, grants, readOnly, onToggleAction, onToggleResource, onToggleModule, onScopeChange }) => {
    const { th } = useLanguage();

    const scopeOptions = (resource) => (resource.supportedScopes || []).map((scope) => ({ value: scope, label: th(`authorizationV2.scopes.${DATA_SCOPE_KEYS[scope]}`) }));

    const grantedCount = (module) => module.resources.reduce((sum, r) => sum + (grants[r.code]?.actions.length || 0), 0);
    const totalCount = (module) => module.resources.reduce((sum, r) => sum + r.actions.length, 0);

    const moduleHeader = (module) => (
        <div className="flex align-items-center gap-2 w-full">
            <i className={MODULE_ICONS[module.code] || 'pi pi-th-large'} />
            <span className="font-semibold">{th(`authorizationV2.modules.${module.code}`, { fallback: module.name })}</span>
            <Tag className="ml-auto" severity={grantedCount(module) > 0 ? 'success' : null} value={`${grantedCount(module)} / ${totalCount(module)}`} />
        </div>
    );

    return (
        <Accordion multiple activeIndex={[0]}>
            {catalog.map((module) => (
                <AccordionTab key={module.code} header={moduleHeader(module)}>
                    {!readOnly && (
                        <div className="flex justify-content-end gap-2 mb-2">
                            <Button type="button" size="small" text icon="pi pi-check-square" label={th('authorizationV2.matrix.selectAll')} onClick={() => onToggleModule(module, true)} />
                            <Button type="button" size="small" text severity="secondary" icon="pi pi-times" label={th('authorizationV2.matrix.clearAll')} onClick={() => onToggleModule(module, false)} />
                        </div>
                    )}
                    <div className="overflow-x-auto">
                        <table className="w-full" style={{ borderCollapse: 'collapse', minWidth: '760px' }}>
                            <thead>
                                <tr className="surface-100">
                                    <th className="text-left p-2">{th('authorizationV2.matrix.screen')}</th>
                                    {PERMISSION_ACTIONS.map((action) => (
                                        <th key={action} className="text-center p-2 white-space-nowrap">
                                            {th(`authorizationV2.actions.${action}`)}
                                        </th>
                                    ))}
                                    <th className="text-left p-2">{th('authorizationV2.matrix.scope')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {module.resources.map((resource) => {
                                    const grant = grants[resource.code];
                                    const available = resource.actions.map((a) => a.action);
                                    const allChecked = available.length > 0 && available.every((a) => grant?.actions.includes(a));
                                    const anyChecked = (grant?.actions.length || 0) > 0;
                                    return (
                                        <tr key={resource.code} className="border-bottom-1 surface-border">
                                            <td className="p-2">
                                                <div className="flex align-items-center gap-2">
                                                    <Checkbox inputId={`row-${resource.code}`} checked={allChecked} disabled={readOnly} onChange={(e) => onToggleResource(resource.code, e.checked)} />
                                                    <label htmlFor={`row-${resource.code}`} className="cursor-pointer">
                                                        <div>{th(`authorizationV2.resources.${resource.code.replace(/\./g, '_')}`, { fallback: resource.name })}</div>
                                                        <small className="text-color-secondary">{resource.code}</small>
                                                    </label>
                                                </div>
                                            </td>
                                            {PERMISSION_ACTIONS.map((action) => (
                                                <td key={action} className="text-center p-2">
                                                    {available.includes(action) ? (
                                                        <Checkbox checked={!!grant?.actions.includes(action)} disabled={readOnly} onChange={() => onToggleAction(resource.code, action)} aria-label={`${resource.code}.${action}`} />
                                                    ) : (
                                                        <span className="text-color-secondary">–</span>
                                                    )}
                                                </td>
                                            ))}
                                            <td className="p-2" style={{ minWidth: '11rem' }}>
                                                <Dropdown
                                                    value={grant?.scope}
                                                    options={scopeOptions(resource)}
                                                    onChange={(e) => onScopeChange(resource.code, e.value)}
                                                    disabled={readOnly || !anyChecked || (resource.supportedScopes || []).length <= 1}
                                                    placeholder={anyChecked ? th('authorizationV2.matrix.selectScope') : '–'}
                                                    className="w-full p-inputtext-sm"
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </AccordionTab>
            ))}
        </Accordion>
    );
};

export default PermissionMatrix;
